<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5">
      <v-row>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="selectedProduct"
            :items="products"
            :item-text="productDisplay"
            item-value="id"
            label="Select Product"
            clearable
            dense
            hide-details
            @update:search-input="fetchProducts"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.start_date"
                label="Start Date"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
                clearable
              />
            </template>
            <v-date-picker
              v-model="filters.start_date"
              @input="startMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.end_date"
                label="End Date"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
                clearable
              />
            </template>
            <v-date-picker
              v-model="filters.end_date"
              @input="endMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="2" class="d-flex align-end">
          <v-btn
            color="primary"
            @click="fetchReport"
            :loading="loading"
            :disabled="!selectedProduct"
            block
          >
            Generate
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <div v-if="goodsReceived.length">
        <h3 class="mb-2">Goods Received (GRN)</h3>
        <v-data-table
          :headers="tableHeaders"
          :items="goodsReceived"
          dense
          class="mb-5"
          :hide-default-footer="true"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.date + item.grn_number">
                <td>{{ item.date }}</td>
                <td>{{ item.grn_number }}</td>
                <td class="text-right">{{ item.quantity }}</td>
              </tr>
              <tr>
                <td colspan="2" class="text-right font-weight-bold">
                  Total Quantity Received
                </td>
                <td class="text-right font-weight-bold">{{ totalReceived }}</td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
      <div v-else-if="selectedProduct && !loading">
        <v-alert type="info" outlined
          >No GRN records found for this product.</v-alert
        >
      </div>

      <div v-if="goodsSold.length">
        <h3 class="mb-2">Goods Sold (Invoice)</h3>
        <v-data-table
          :headers="tableHeaders"
          :items="goodsSold"
          dense
          :hide-default-footer="true"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.date + item.invoice_number">
                <td>{{ item.date }}</td>
                <td>{{ item.invoice_number }}</td>
                <td class="text-right">{{ item.quantity }}</td>
              </tr>
              <tr>
                <td colspan="2" class="text-right font-weight-bold">
                  Total Quantity Sold
                </td>
                <td class="text-right font-weight-bold">{{ totalSold }}</td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
      <div v-else-if="selectedProduct && !loading">
        <v-alert type="info" outlined
          >No Invoice records found for this product.</v-alert
        >
      </div>
    </v-card>
  </div>
</template>

<script>
import { BatchService } from "@/services/batch-service.js";
import { ReportService } from "@/services/report-service.js";

export default {
  name: "ProductMovementReport",
  data() {
    return {
      products: [],
      selectedProduct: null,
      goodsReceived: [],
      goodsSold: [],
      loading: false,
      startMenu: false,
      endMenu: false,
      filters: {
        start_date: null,
        end_date: null,
      },
      tableHeaders: [
        { text: "Date", value: "date" },
        { text: "Number", value: "number" },
        { text: "Quantity", value: "quantity", align: "right" },
      ],
      grnHeaders: [
        { text: "Date", value: "date" },
        { text: "GRN Number", value: "grn_number" },
        { text: "Quantity Received", value: "quantity" },
      ],
      invoiceHeaders: [
        { text: "Date", value: "date" },
        { text: "Invoice Number", value: "invoice_number" },
        { text: "Quantity Sold", value: "quantity" },
      ],
      productSearchTimeout: null,
    };
  },
  computed: {
    totalReceived() {
      return this.goodsReceived.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0,
      );
    },
    totalSold() {
      return this.goodsSold.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0,
      );
    },
  },
  created() {
    this.fetchProducts();
  },
  methods: {
    productDisplay(item) {
      if (!item) return "";
      if (item.name && item.product_code) {
        return `${item.name} (${item.product_code})`;
      }
      return item.product_code || item.name || "";
    },
    async fetchProducts(search = "") {
      try {
        const products = await BatchService.getProducts(search);
        this.products = products.data.data;
      } catch (err) {
        this.products = [];
      }
    },
    async fetchReport() {
      if (!this.selectedProduct) return;
      this.loading = true;
      this.goodsReceived = [];
      this.goodsSold = [];
      try {
        const params = {
          product_id: this.selectedProduct,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        };
        const response = await ReportService.getGrnItemsByProduct(params);

        const goodsReceivedRaw =
          response.goods_received ||
          (response.data && response.data.goods_received) ||
          [];
        const goodsSoldRaw =
          response.goods_sold ||
          (response.data && response.data.goods_sold) ||
          [];
        this.goodsReceived = goodsReceivedRaw.map((item) => ({
          date: item.grn_date || item.created_at,
          grn_number: item.grn_number,
          quantity: item.quantity_received,
        }));
        this.goodsSold = goodsSoldRaw.map((item) => ({
          date: item.invoice_date || item.created_at,
          invoice_number: item.invoice_number,
          quantity: item.quantity,
        }));
      } catch (e) {
        this.goodsReceived = [];
        this.goodsSold = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
h3 {
  font-weight: 600;
}
</style>
